import { ref, Ref } from 'vue';
import PricingService from 'services/PricingService';
import OfferingService from 'services/OfferingService';
import CurrencyService from 'services/CurrencyService';
import ProductGroupService from 'services/ProductGroupService';

export interface NormalizedOffering {
  id: number;
  name: string;
  description: string;
  thumbnail: string | null;
  productGroupId: number;
  inStock: boolean;
}

export interface NormalizedCurrency {
  iso: string;
  name: string;
  symbol: string;
}

export interface NormalizedProductGroup {
  id: number;
  name: string;
}

export interface PriceRecord {
  id: number;
  offeringid: number;
  currency: string;
  price_base: number | null;
  price_base_from: number | null;
  price_page: number | null;
  price_page_from: number | null;
}

export interface PricingDataState {
  prices: Ref<PriceRecord[]>;
  offerings: Ref<NormalizedOffering[]>;
  currencies: Ref<NormalizedCurrency[]>;
  productGroups: Ref<NormalizedProductGroup[]>;
  isLoading: Ref<boolean>;
  error: Ref<string | null>;
  loadData: () => Promise<void>;
  getOfferingById: (id: number) => NormalizedOffering | undefined;
  getCurrencyByIso: (iso: string) => NormalizedCurrency | undefined;
}

export function usePricingData(): PricingDataState {
  const prices = ref<PriceRecord[]>([]);
  const offerings = ref<NormalizedOffering[]>([]);
  const currencies = ref<NormalizedCurrency[]>([]);
  const productGroups = ref<NormalizedProductGroup[]>([]);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  const normalizeOffering = (raw: any): NormalizedOffering => ({
    id: raw.id,
    name: raw.name || '',
    description: raw.description || '',
    thumbnail: raw.thumbnail || null,
    productGroupId: raw.groupid ?? raw.productgroupid ?? raw.productGroupId ?? raw.product_group_id ?? 0,
    inStock: raw.instock === 1 || raw.instock === true || raw.inStock === 1 || raw.inStock === true,
  });

  const normalizeCurrency = (raw: any): NormalizedCurrency => ({
    iso: raw.iso || raw.code || '',
    name: raw.name || '',
    symbol: raw.symbol || raw.iso || '',
  });

  const normalizeProductGroup = (raw: any): NormalizedProductGroup => ({
    id: raw.id,
    name: raw.name || `Group ${raw.id}`,
  });

  const loadData = async () => {
    isLoading.value = true;
    error.value = null;
    
    try {
      const params = new URLSearchParams({ limit: '0' });
      const [pricesData, offeringsData, currenciesData, groupsData] = await Promise.all([
        PricingService.getAll(params),
        OfferingService.getAll(params),
        CurrencyService.getAll(params),
        ProductGroupService.getAll(params),
      ]);
      
      prices.value = pricesData || [];
      offerings.value = (offeringsData || []).map(normalizeOffering);
      currencies.value = (currenciesData || []).map(normalizeCurrency);
      productGroups.value = (groupsData || []).map(normalizeProductGroup);
    } catch (err: any) {
      error.value = err.message || 'Failed to load pricing data';
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const getOfferingById = (id: number): NormalizedOffering | undefined => {
    return offerings.value.find(o => o.id === id);
  };

  const getCurrencyByIso = (iso: string): NormalizedCurrency | undefined => {
    return currencies.value.find(c => c.iso === iso);
  };

  return {
    prices,
    offerings,
    currencies,
    productGroups,
    isLoading,
    error,
    loadData,
    getOfferingById,
    getCurrencyByIso,
  };
}
